import styled from 'styled-components/macro';
import { rgba } from 'polished';
import { Link as RouterLink } from 'react-router-dom';

import { ThemeProps } from 'types/styled';
import { boxShadow } from 'styles/base-classes';
import modularScale from 'styles/helpers/modular-scale';

import ArtworkBase from 'components/base/Artwork/Artwork';
import Layout from 'components/styled/Layout/Layout';
import colors from 'styles/themes/colors';
import variables from 'styles/themes/variables';
import { ButtonBack } from 'components/TopicFilters/ViewRelatedMenu/ViewRelatedMenu.styles';
import Box from 'components/elements/Box';
import {
  ButtonContent,
  ButtonWrapper,
  Placeholder,
} from 'components/base/Dropdown/shared/styles';
import { Figure } from 'components/base/Menu/Menu';
import { ItemWrapper } from 'components/base/Menu/Menu';
import { MenuButton } from 'components/base/Menu/index';

export const BellButton = styled.button<ThemeProps<'button'>>`
  margin: 0;
  width: 100%;
  min-width: ${({ theme }): string => theme.topbarHeight};
  height: 100%;
  position: relative;
  background: ${(p: any): string =>
    p.isOpen ? 'rgba(0, 0, 0, 0.1)' : 'initial'};
  transition: background ${variables.transitionDuration}
    ${variables.timingFunction};

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: rgba(0, 0, 0, 0.2);
  }

  ${({ isMobileScreen, isOpen }): string | undefined =>
    isMobileScreen &&
    `border-radius: 50%; height: 40px; width: 40px; min-width: 40px;
      div {
        width: 26px;
      }
      &:hover,
      &:focus {
        background: ${isOpen ? ' rgba(0, 0, 0, 0.1)' : 'none'};
      }
      `}

  @media only screen and (max-width: 25em) {
    min-width: 2em;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${colors.NotificationOrange};
    position: absolute;
    top: -0.4em;
    right: 0;
    font-size: ${modularScale(-2)};
    font-family: ${variables.numbersTypography};
    width: 1.7em;
    height: 1.7em;
    color: white;
  }

  svg {
    transform: scale(1.1);
  }
`;

export const Scroll = styled.div<ThemeProps<'div'>>`
  overflow-y: scroll;
  overflow-x: hidden;
  height: auto;
  min-height: 270px;

  width: 100%;
  height: ${({ isMobileScreen }): string =>
    isMobileScreen ? 'unset' : '82vh'};

  @media screen and (min-width: 50em) {
    min-width: 25em;
    max-height: 80vh;
    height: initial;
  }
`;

export const Fixed = styled.div<ThemeProps<'div'>>`
  padding: ${variables.spacing.small} ${variables.spacing.medium};
  box-shadow: 0 3px 3px ${rgba(colors.ShadowBlack, 0.05)};

  h2 {
    margin: 0;
    margin-top: ${variables.spacing.small};
  }
`;

export const Absolute = styled.div<any>`
  position: fixed;
  background: white;
  right: 0;
  ${boxShadow};
  left: 0;
  top: 3.5em;
  bottom: 0;
  overflow: hidden;

  g {
    fill: none;
    color: none;

    path {
      fill: #001247;
    }
  }

  ${({ isMobileScreen }): string =>
    isMobileScreen ? 'margin: 5px; top: 4.5em; min-height: 100vh;' : ''}

  @media screen and (min-width: 50em) {
    position: absolute;
    right: 0;
    left: auto;
    bottom: auto;
    top: 1.75em;
  }
`;

export const AbsoluteMobile = styled('div')<any>`
  right: 0;
  left: 0;
  top: 3.5em;
  overflow: hidden;
  height: 100%;
  background: none;
  ${({ isMobileScreen }): string =>
    isMobileScreen
      ? 'margin: 0; background: #f3f4f4; position: fixed; top: 4.5em; min-height: 100vh;'
      : ''}
`;

export const SliderContainer = styled('div')`
  overflow: hidden;
  flex: 1 1 100%;
  height: 100%;
  width: 100%;

  @media screen and (min-width: 50em) {
    width: 400px;
  }
`;

export const Slider = styled('div')`
  width: 200%;
  display: flex;
  flex-grow: 1;
  will-change: transform;
  transition: ${({ theme }): any => `transform 300ms ${theme.timingFunction}`};
  transform: ${({ detailSection }: { detailSection: any }): any =>
    detailSection ? 'translateX(-50%)' : 'none'};
  height: 100%;

  @media screen and (min-width: 36em) {
    align-items: flex-start;
  }
`;

export const Slide = styled('div')`
  flex: 0 0 50%;
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const Flex = styled.div<ThemeProps<'div'>>`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const ArtistLink = styled.button<ThemeProps<'button'>>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: default;
  padding: ${variables.spacing.small} ${variables.spacing.medium};
  border-top: 1px solid ${colors.LightGrey};
  width: 100%;

  &:hover,
  &:focus {
    background: ${rgba(colors.VeryLightGrey, 0.5)};
  }
  &:active {
    background: ${rgba(colors.LightGrey, 0.5)};
  }

  svg[aria-hidden] {
    width: ${modularScale(0)};
    height: ${modularScale(0)};
  }

  h3 {
    text-align: left;
    margin: 0;
    margin-left: ${variables.spacing.xxsmall};
    font-size: ${modularScale(1)};

    span {
      color: ${colors.CasperGrey};
      font-weight: normal;
    }
  }
`;

export const ArtistWrapper = styled.div<ThemeProps<'div'>>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: default;
  padding: ${variables.spacing.small} ${variables.spacing.medium};
  border-top: 1px solid ${colors.LightGrey};

  h2 {
    margin: 0;
    margin-left: ${variables.spacing.small};
    font-size: ${modularScale(1)};

    span {
      color: ${colors.CasperGrey};
      font-weight: normal;
    }
  }
`;

export const TimeHeading = styled.h3<ThemeProps<'h3'>>`
  margin: ${variables.spacing.medium} 0;
  margin-left: ${variables.spacing.medium};
  font-size: ${modularScale(0)};
  font-weight: normal;
`;

export const Artwork = styled(ArtworkBase)`
  width: ${modularScale(5)} !important;
  height: ${modularScale(5)} !important;
  margin-right: 0.5em;
`;

export const ListItem = styled.li<ThemeProps<'li'>>`
  display: flex;
  flex-direction: column;
  margin: ${variables.spacing.small} 0;
  margin-left: 4.2em;
  margin-right: ${variables.spacing.small};
  position: relative;

  &::before {
    content: '';
    display: ${(p: any): string => (p.withBullet ? 'block' : 'none')};
    width: 0.7em;
    height: 0.7em;
    border-radius: 50%;
    background: ${colors.Blue};
    position: absolute;
    left: -2.25em;
    top: 0.5em;
  }
`;

export const Meta = styled.div<ThemeProps<'div'>>`
  font-size: ${modularScale(-1)};
  color: ${colors.CasperGrey};
  display: flex;
  align-items: center;

  img {
    margin: 0 ${variables.spacing.xxsmall};
  }
`;

export const Button = styled.button<ThemeProps<'button'>>`
  color: ${colors.AccentGreen};
  text-align: left;
  cursor: default;
  padding: 0;
`;

export const Link = styled(RouterLink)`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const NoDataMessage = styled.p<ThemeProps<'p'>>`
  color: ${colors.CasperGrey};
  padding: ${variables.spacing.small} 0;
  margin: 0;
`;

export const BellBox = styled(Layout.Center)`
  height: 3rem;
  svg {
    transform: scale(0.8);
  }
`;

export const BackButton = styled(ButtonBack)`
  margin: 1.3125em;
`;

export const SearchFieldWrapper = styled(Box)`
  svg {
    transform: scale(1.2);
    & > g {
      color: ${colors.AccentGreen};
      & > path {
        fill: ${colors.AccentGreen};
        &:first-of-type {
          fill: transparent;
        }
      }
    }
  }
`;

export const StyledDropdown = styled(Box)<ThemeProps<'div'>>`
  ${MenuButton}:hover, ${MenuButton}:focus {
    & ${ButtonWrapper} {
      background: ${colors.White};
      color: #000000;
      border: none;
    }
    svg.dropdown-chevron {
      color: ${colors.DeepBlue};
    }
  }

  ${Figure} {
    top: calc(100% + 10px);
  }

  ${ButtonContent} {
    flex: 0 1 auto;
  }

  ${ButtonWrapper} {
    padding: 0 0 0 1.5em;
    justify-content: flex-start;
    background: ${colors.White};
    font-weight: 400;

    &:hover,
    &:active,
    &:focus {
      background: ${colors.White};
    }
  }

  ${Placeholder}, ${ItemWrapper} span {
    color: ${colors.ShadowBlack};
    text-transform: initial;
  }

  svg.dropdown-chevron {
    color: ${colors.DeepBlue};
  }
`;
